import React, { useEffect, useState } from "react";
import "./Rch.css";
import { MenuItem, Select, TextField, Button, Box, CircularProgress } from "@mui/material";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import { toast } from "react-toastify";
import { rchAPI } from "../../services/rch.api";

// ─── Validation Schema ───────────────────────────────────────────────────────
const validationSchema = Yup.object({
    primaryMob: Yup.string()
        .matches(/^[6-9]\d{9}$/, "Contact No. must start with 6-9 and be exactly 10 digits")
        .required("Primary Phone Number is required"),
    name: Yup.string().required("Name is required"),
    abhaId: Yup.string().required("ABHA ID is required"),
    genderId: Yup.string().required("Gender is required"),
    beneficiaryDob: Yup.string().required("Date of Birth is required"),
});

const RCHRegistration = ({ onClose, checkJananiBeneficiaryExist }) => {
    const [initialValues, setInitialValues] = useState({
        abhaId: "",
        name: "",
        genderId: "",
        beneficiaryDob: "",
        primaryMob: "",
        facilityId: "",
        parentUserId: "",
    });

    useEffect(() => {
        const userDetails = localStorage.getItem("beneficiaryDetails") || localStorage.getItem("orignalBeneficiaryData");
        const userLoginDetailsStr = localStorage.getItem("JANANI_loggedinUserdetails");

        if (userDetails) {
            try {
                const abhaData = JSON.parse(userDetails);
                const userLoginDetails = userLoginDetailsStr ? JSON.parse(userLoginDetailsStr) : {};

                const nameParts = (abhaData.name || "").split(' ');
                const firstName = nameParts[0] || "";
                const lastName = nameParts.slice(1).join(' ') || "";
                const fullName = `${firstName} ${lastName}`.trim();

                const abhaId = abhaData.originalabha ? abhaData.originalabha.replace(/-/g, "") : "";
                const mobile = abhaData.mobile || "";
                const beneficiaryDob = abhaData.dob || "";
                const genderId = abhaData?.gender === "Male" ? "627c533a-110e-475b-a1f0-3f19d89d6e94" : abhaData?.gender === "Female" ? "182c6f20-5039-4ff7-9571-c321b8fb02cb" : "";

                setInitialValues({
                    name: fullName,
                    abhaId: abhaId,
                    primaryMob: "",
                    beneficiaryDob: beneficiaryDob.split('-').reverse().join('-'), // Convert DD-MM-YYYY to YYYY-MM-DD
                    genderId: genderId,
                    facilityId: userLoginDetails?.facilityId || "",
                    parentUserId: userLoginDetails?.userId || "",
                });
            } catch (error) {
                console.error("Error parsing user details:", error);
            }
        }
    }, []);

    const handleSubmit = async (values, { setSubmitting }) => {
        try {
            const response = await rchAPI.addRchBeneficiary(values);

            if (response?.success) {
                toast.success(response?.message || "Beneficiary added successfully");
                if (checkJananiBeneficiaryExist) {
                    checkJananiBeneficiaryExist();
                }
                onClose();
            } else {
                toast.error(response?.message || "Failed to add beneficiary");
            }
        } catch (error) {
            const errorMessage = error?.response?.data?.message || "Something went wrong. Please try again.";
            toast.error(errorMessage);
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <section className="rch-registration-section bg-white rounded shadow-sm p-4 mt-3 position-relative">
            <div className="leftHeaderBar">
                <h4>
                    <span>
                        <img
                            src={"/images/Rchicon.svg"}
                            alt=""
                            style={{ width: "50px", marginRight: "15px" }}
                        />
                    </span>
                    JANANI Registration
                </h4>
            </div>

            <Formik
                initialValues={initialValues}
                validationSchema={validationSchema}
                onSubmit={handleSubmit}
                enableReinitialize
            >
                {({ values, errors, touched, handleChange, handleBlur, setFieldValue, isSubmitting }) => (
                    <Form>
                        <div className="row g-3">
                            {/* ABHA Number */}
                            <div className="col-md-4">
                                <label className="form-label">
                                    ABHA Number<span className="text-danger">*</span>
                                </label>
                                <TextField
                                    fullWidth
                                    name="abhaId"
                                    value={values?.abhaId}
                                    variant="outlined"
                                    size="small"
                                    disabled
                                    error={touched?.abhaId && Boolean(errors?.abhaId)}
                                    helperText={touched?.abhaId && errors?.abhaId}
                                />
                            </div>

                            {/* Beneficiary Name */}
                            <div className="col-md-4">
                                <label className="form-label">
                                    Beneficiary Name<span className="text-danger">*</span>
                                </label>
                                <TextField
                                    fullWidth
                                    name="name"
                                    value={values?.name}
                                    variant="outlined"
                                    size="small"
                                    disabled
                                    error={touched?.name && Boolean(errors?.name)}
                                    helperText={touched?.name && errors?.name}
                                />
                            </div>

                            {/* Gender */}
                            <div className="col-md-4">
                                <label className="form-label">
                                    Gender<span className="text-danger">*</span>
                                </label>
                                <Select
                                    name="genderId"
                                    value={values?.genderId}
                                    onChange={handleChange}
                                    displayEmpty
                                    fullWidth
                                    size="small"
                                    disabled
                                >
                                    <MenuItem value="627c533a-110e-475b-a1f0-3f19d89d6e94">Male</MenuItem>
                                    <MenuItem value="182c6f20-5039-4ff7-9571-c321b8fb02cb">Female</MenuItem>
                                    <MenuItem value="">Other</MenuItem>
                                </Select>
                                {touched?.genderId && errors?.genderId && (
                                    <div className="text-danger small mt-1">{errors?.genderId}</div>
                                )}
                            </div>

                            {/* DOB */}
                            <div className="col-md-4">
                                <label className="form-label">
                                    Beneficiary DOB<span className="text-danger">*</span>
                                </label>
                                <TextField
                                    fullWidth
                                    type="date"
                                    name="beneficiaryDob"
                                    value={values?.beneficiaryDob}
                                    variant="outlined"
                                    size="small"
                                    disabled
                                    error={touched?.beneficiaryDob && Boolean(errors?.beneficiaryDob)}
                                    helperText={touched?.beneficiaryDob && errors?.beneficiaryDob}
                                />
                            </div>

                            {/* Primary Phone */}
                            <div className="col-md-4">
                                <label className="form-label">
                                    Primary Phone Number<span className="text-danger">*</span>
                                </label>
                                <TextField
                                    fullWidth
                                    name="primaryMob"
                                    value={values?.primaryMob}
                                    onChange={(e) => {
                                        const numericOnly = e.target.value.replace(/\D/g, "").slice(0, 10);
                                        setFieldValue("primaryMob", numericOnly);
                                    }}
                                    onBlur={handleBlur}
                                    variant="outlined"
                                    size="small"
                                    inputProps={{ inputMode: "numeric", maxLength: 10 }}
                                    error={touched?.primaryMob && Boolean(errors?.primaryMob)}
                                    helperText={touched?.primaryMob && errors?.primaryMob}
                                />
                            </div>
                        </div>

                        {/* Footer Buttons */}
                        <div className="d-flex justify-content-end gap-3 mt-4">
                            <Button
                                variant="outline-secondary"
                                className="rounded-pill px-4"
                                onClick={onClose}
                                style={{ border: "1px solid #ccc", color: "#666" }}
                                disabled={isSubmitting}
                            >
                                Cancel
                            </Button>
                            <Button
                                type="submit"
                                variant="contained"
                                className="btn-info text-white rounded-pill px-4"
                                style={{ background: "#0dcaf0", border: "none" }}
                                disabled={isSubmitting}
                            >
                                {isSubmitting ? <CircularProgress size={24} color="inherit" /> : "Submit"}
                            </Button>
                        </div>
                    </Form>
                )}
            </Formik>

            <div className="closeDive nikshayclose" onClick={onClose}>
                <img src="/images/closing.svg" alt="Close" />
            </div>
        </section>
    );
};

export default RCHRegistration;
